// Jinx Fishbones VFX: a properly shaped rocket (nose cone + body + fins + exhaust)
// that flies toward the target and explodes on impact.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const TRAVEL_MS = 220;
const IMPACT_MS = 280;
const TOTAL = TRAVEL_MS + IMPACT_MS;

const BODY_W = 5;
const BODY_H = 18;
const CONE_H = 9;
const FIN_H = 7;
const TRAIL_MAX_AGE = 130;
const SMOKE_MAX_AGE = 420;

interface Spark {
  angle: number;
  speed: number;
  size: number;
}

interface TrailParticle {
  x: number;
  y: number;
  age: number;
}

interface SmokeParticle {
  x: number;
  y: number;
  age: number;
  baseR: number;
  drift: number;
}

export class JinxRocketVFX implements Animation {
  private elapsed = 0;
  private angle: number;
  private sparks: Spark[];
  private trail: TrailParticle[] = [];
  private smoke: SmokeParticle[] = [];
  private impactFired = false;
  private smokeTimer = 0;

  constructor(
    private from: Point,
    private to: Point,
    private onImpact?: () => void,
  ) {
    this.angle = Math.atan2(to.y - from.y, to.x - from.x);
    this.sparks = Array.from({ length: 12 }, () => ({
      angle: Math.random() * Math.PI * 2,
      speed: 50 + Math.random() * 70,
      size: 2 + Math.random() * 3,
    }));
  }

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);

    if (!this.impactFired && this.elapsed >= TRAVEL_MS) {
      this.impactFired = true;
      this.onImpact?.();
    }

    if (this.elapsed < TRAVEL_MS) {
      const t = Easing.easeOutQuad(this.elapsed / TRAVEL_MS);
      const x = this.from.x + (this.to.x - this.from.x) * t;
      const y = this.from.y + (this.to.y - this.from.y) * t;
      this.trail.push({ x, y, age: 0 });

      this.smokeTimer += dt;
      if (this.smokeTimer >= 28) {
        this.smokeTimer = 0;
        // spawn smoke slightly behind the rocket (opposite travel direction)
        const perpDrift = (Math.random() - 0.5) * 6;
        const perpX = -Math.sin(this.angle) * perpDrift;
        const perpY =  Math.cos(this.angle) * perpDrift;
        this.smoke.push({
          x: x - Math.cos(this.angle) * (BODY_H * 0.6) + perpX,
          y: y - Math.sin(this.angle) * (BODY_H * 0.6) + perpY,
          age: 0,
          baseR: 3 + Math.random() * 2,
          drift: (Math.random() - 0.5) * 18,
        });
      }
    }

    for (const p of this.trail) p.age += dt;
    this.trail = this.trail.filter((p) => p.age < TRAIL_MAX_AGE);

    for (const p of this.smoke) p.age += dt;
    this.smoke = this.smoke.filter((p) => p.age < SMOKE_MAX_AGE);
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.elapsed <= TRAVEL_MS) {
      this.drawTravel(ctx);
    } else {
      this.drawLingerSmoke(ctx);
      this.drawImpact(ctx);
    }
  }

  private drawLingerSmoke(ctx: CanvasRenderingContext2D): void {
    if (this.smoke.length === 0) return;
    ctx.save();
    for (const p of this.smoke) {
      const life = p.age / SMOKE_MAX_AGE;
      const a = (1 - life) * 0.3;
      const r = p.baseR + life * 14;
      const perpX = -Math.sin(this.angle) * p.drift * life;
      const perpY =  Math.cos(this.angle) * p.drift * life;
      ctx.globalAlpha = a;
      ctx.fillStyle = "#888888";
      ctx.shadowBlur = 0;
      ctx.beginPath();
      ctx.arc(p.x + perpX, p.y + perpY, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  private drawTravel(ctx: CanvasRenderingContext2D): void {
    // Smoke trail (drawn first so fire sits on top)
    ctx.save();
    for (const p of this.smoke) {
      const life = p.age / SMOKE_MAX_AGE;
      const a = (1 - life) * 0.45;
      const r = p.baseR + life * 14;
      const perpX = -Math.sin(this.angle) * p.drift * life;
      const perpY =  Math.cos(this.angle) * p.drift * life;
      ctx.globalAlpha = a;
      ctx.fillStyle = life < 0.3 ? "#bbbbbb" : "#888888";
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.beginPath();
      ctx.arc(p.x + perpX, p.y + perpY, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    // Fire trail
    ctx.save();
    for (const p of this.trail) {
      const a = 1 - p.age / TRAIL_MAX_AGE;
      const r = 3.5 * a;
      ctx.globalAlpha = a * 0.75;
      ctx.fillStyle = p.age < TRAIL_MAX_AGE * 0.35 ? "#ffee44" : "#ff6600";
      ctx.shadowColor = "#ff6600";
      ctx.shadowBlur = 6;
      ctx.beginPath();
      ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    const t = Easing.easeOutQuad(this.elapsed / TRAVEL_MS);
    const x = this.from.x + (this.to.x - this.from.x) * t;
    const y = this.from.y + (this.to.y - this.from.y) * t;

    ctx.save();
    ctx.translate(x, y);
    // +π/2 so the nose (top of rocket in local space) points along travel direction
    ctx.rotate(this.angle + Math.PI / 2);
    ctx.shadowColor = "#ff6600";
    ctx.shadowBlur = 14;

    // Body
    ctx.fillStyle = "#44aa44";
    ctx.fillRect(-BODY_W, -BODY_H / 2, BODY_W * 2, BODY_H);

    // Nose cone (points toward -Y in local space = travel direction)
    ctx.fillStyle = "#ff4400";
    ctx.beginPath();
    ctx.moveTo(-BODY_W, -BODY_H / 2);
    ctx.lineTo(0, -BODY_H / 2 - CONE_H);
    ctx.lineTo(BODY_W, -BODY_H / 2);
    ctx.closePath();
    ctx.fill();

    // Left fin
    ctx.fillStyle = "#226622";
    ctx.beginPath();
    ctx.moveTo(-BODY_W, BODY_H / 2);
    ctx.lineTo(-BODY_W - FIN_H, BODY_H / 2 + FIN_H);
    ctx.lineTo(-BODY_W, BODY_H / 2 - FIN_H * 0.5);
    ctx.closePath();
    ctx.fill();

    // Right fin
    ctx.beginPath();
    ctx.moveTo(BODY_W, BODY_H / 2);
    ctx.lineTo(BODY_W + FIN_H, BODY_H / 2 + FIN_H);
    ctx.lineTo(BODY_W, BODY_H / 2 - FIN_H * 0.5);
    ctx.closePath();
    ctx.fill();

    // Exhaust glow
    ctx.globalAlpha = 0.9;
    ctx.fillStyle = "#ffee44";
    ctx.shadowColor = "#ffee44";
    ctx.shadowBlur = 22;
    ctx.beginPath();
    ctx.arc(0, BODY_H / 2 + 3, BODY_W * 0.9, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  private drawImpact(ctx: CanvasRenderingContext2D): void {
    const t = (this.elapsed - TRAVEL_MS) / IMPACT_MS;
    const fadeOut = 1 - Easing.easeInQuad(t);
    const ringT = Easing.easeOutQuad(t);

    ctx.save();
    ctx.shadowColor = "#ff6600";
    ctx.shadowBlur = 28;

    // Outer explosion ring
    const ringR = 24 * (1 + ringT * 3.5);
    ctx.globalAlpha = fadeOut * 0.85;
    ctx.strokeStyle = "#ff8800";
    ctx.lineWidth = 3.5;
    ctx.beginPath();
    ctx.arc(this.to.x, this.to.y, ringR, 0, Math.PI * 2);
    ctx.stroke();

    // Inner orange fill
    ctx.globalAlpha = fadeOut * 0.35;
    ctx.fillStyle = "#ff4400";
    ctx.beginPath();
    ctx.arc(this.to.x, this.to.y, ringR * 0.55, 0, Math.PI * 2);
    ctx.fill();

    // Central flash
    const flashR = 18 * (1.3 - t * 1.3);
    if (flashR > 0) {
      ctx.globalAlpha = fadeOut;
      ctx.fillStyle = "#ffeeaa";
      ctx.shadowBlur = 30;
      ctx.beginPath();
      ctx.arc(this.to.x, this.to.y, flashR, 0, Math.PI * 2);
      ctx.fill();
    }

    // Spark particles
    for (const p of this.sparks) {
      const dist = p.speed * (t * IMPACT_MS / 1000);
      const px = this.to.x + Math.cos(p.angle) * dist;
      const py = this.to.y + Math.sin(p.angle) * dist;
      ctx.globalAlpha = fadeOut * 0.9;
      ctx.shadowBlur = 8;
      ctx.shadowColor = "#ff6600";
      ctx.fillStyle = t < 0.4 ? "#ffdd00" : "#ff6600";
      ctx.beginPath();
      ctx.arc(px, py, p.size * (1 - t * 0.6), 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL && this.smoke.length === 0;
  }
}
